import {Injectable} from '@angular/core';
import {GeneralService} from './general.service';
import {AuthService} from './auth.service';
import {
    BehaviorSubject, catchError,
    concat,
    EMPTY,
    expand,
    forkJoin,
    Observable, of,
    reduce,
    Subject,
    switchMap,
    takeUntil,
    tap, throwError
} from 'rxjs';
import {NICEFile} from '../models/NICE_File.model';
import {map} from 'rxjs/operators';
import {NICESkill} from '../models/NICE_Skill.model';
import {NICEDisposition} from '../models/NICE_Disposition.model';
import {NICEDispClassification} from '../models/NICE_DispClassification.model';
import {NICECampaign} from '../models/NICE_Campaign.model';
import {NICEPOC} from '../models/NICE_POC.model';
import {NICEUnavailableCode} from '../models/NICE_UnavailableCode.model';
import {NICEMediaTypes} from '../models/NICE_MediaTypes.model';
import {NICEHoursOfOperation} from '../models/NICE_HoursOfOperation.model';
import {UtilityService} from './utility.service';
import {Router} from '@angular/router';
import {NICETeam} from '../models/NICE_Team.model';
import {NICEDialerGeneralSettings} from '../models/NICE_DialerGeneralSettings.model';
import {NICEDialerCPASettings} from '../models/NICE_DialerCPASettings.model';
import {NICEDialerXSSettings} from '../models/NICE_DialerXSSettings.model';
import {NICEDialerDeliverySettings} from '../models/NICE_DialerDeliverySettings.model';
import {NICEDialerRetrySettings} from '../models/NICE_DialerRetrySettings.model';
import {NICEDialerScheduleSettings} from '../models/NICE_DialerScheduleSettings.model';
import {NICEDialerTimezoneSettings} from '../models/NICE_DialerTimezoneSettings.model';
import {NICEAgent} from '../models/NICE_Agent.model';
import {NICETimeZone} from '../models/NICE_TimeZones.model';
import {NICECountry} from '../models/NICE_country.model';
import {NICEAgentState} from '../models/NICE_AgentState';
import {NICESkillAgent} from '../models/NICE_SkillAgent.model';
import {NICECallingLists} from '../models/NICE_CallingLists';
import {NICEDataDownload} from '../models/NICE_DataDownload.model';
import {AACFileInfo} from '../models/AAC_FileInfo';
import {NICEDNCGroup} from '../models/NICE_DNCGroup.model';
import {NICEDNCRecord} from '../models/NICE_DNCRecord.model';
import {NICEAddressBook} from '../models/NICE_AddressBook.model';
import {NICEDNCScrubbedSkills} from '../models/NICE_DNCScrubbedSkills';
import {NICEDNCContributingSkills} from '../models/NICE_DNCContributingSkills.model';
import {NICEState} from '../models/NICE_State.model';
import {UserService} from './user.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DateTime} from 'luxon';
import {AACUser} from '../models/AAC_User.model';
import {NICEDialerListMgmtSettings} from '../models/NICE_DialerListMgmtSettings.model';
import {NICEDialerParameters} from "../models/NICE_DialerParameters.model";
import { NICEDigitalTag } from 'app/models/NICE_DigitalTag.model';

@Injectable({
    providedIn: 'root'
})
export class IcDataService {
    agentStates: BehaviorSubject<NICEAgentState[]> = new BehaviorSubject<NICEAgentState[]>([]);
    private agentsMap: Map<number, NICEAgent> = new Map();
    private skillsMap: Map<number, NICESkill> = new Map();
    private dispositionsMap: Map<number, NICEDisposition> = new Map<number, NICEDisposition>();
    public inContactTime: DateTime;
    private skillsLastUpdatedDate: DateTime = DateTime.fromISO('2000-01-01');
    private agentsLastUpdatedDate: DateTime = DateTime.fromISO('2000-01-01');
    private dispositionsLastUpdatedDate: DateTime = DateTime.fromISO('2000-01-01');

    private userFilterValue = '';
    private skillFilterValue = '';
    private campaignFilterValue = '';
    private scriptFilterValue = '';
    private digitalTagFilterValue= '';
    private dNCnumberValue = '';
    private dNCgroupValue = '';
    private currentUser: AACUser;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private mediaTypes: NICEMediaTypes[] = [];


    constructor(public genHttp: GeneralService,
                public auth: AuthService,
                public utility: UtilityService,
                private router: Router,
                private _snackBar: MatSnackBar,
                private _currentUser: UserService,
    ) {
        this._currentUser.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: AACUser) => {
                this.currentUser = user;
            });
    }


    public initializeData(){
        this.agentStates.complete();
        this.agentStates.next([]);
        this.agentsMap.clear();
        this.skillsMap.clear();
        this.dispositionsMap.clear();
        this.inContactTime = null;
        this.skillsLastUpdatedDate = DateTime.fromISO('1900-01-01');
        this.agentsLastUpdatedDate = DateTime.fromISO('1900-01-01');
        this.dispositionsLastUpdatedDate = DateTime.fromISO('1900-01-01');
        return forkJoin({
            skills: this.getSkills(),
            agents: this.getAgentList(),
            mediaTypes: this.getMediaTypes()}
        )
    }


    updateServerTime(): Promise<string> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/server-time')
                .subscribe((response) => {
                    this.inContactTime = DateTime.fromISO(response.ServerTime);
                    resolve(response.ServerTime);
                }, reject);
        });
    }

    aacFileListData(path?: string): Promise<AACFileInfo[]> {
        const buId = this.auth.businessUnit.businessUnitId;
        return new Promise((resolve, reject) => {
            const searchPayload = {filePath: path};
            const URL = 'licenseURL/registeredBusinessUnit/' + buId + '/files/path';
            this.genHttp.icPost(URL, JSON.stringify(searchPayload))
            .pipe(map((response: any) => response.businessUnitFileTranscriptions.map(data => new AACFileInfo(data))))
            .subscribe((response: any) => {
                resolve(response);
            }, reject);
        });
    }

    aacBUTranscriptions(path?: string): Promise<AACFileInfo[]> {
        const buId = this.auth.businessUnit.businessUnitId;
        return new Promise((resolve, reject) => {
            const searchPayload = {filePath: path};
            const URL = 'licenseURL/registeredBusinessUnit/' + buId + '/files';
            this.genHttp.icGet(URL)
                .pipe(map((response: any) => response.businessUnitFileTranscriptions.map(data => new AACFileInfo(data))))
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    aacUpdateFileInfo(fileInfo: AACFileInfo): Promise<AACFileInfo> {
        //console.log(this.currentUser);
        fileInfo.businessUnitId = this.currentUser.aacBusinessUnitId.toString() ;
        fileInfo.insUser = this.currentUser.aacUserName;
        return new Promise((resolve, reject) => {
            const URL = 'licenseURL/registeredBusinessUnit/file';
            this.genHttp.icPost(URL, JSON.stringify(fileInfo))
                .pipe(map((response: any) => new AACFileInfo(response.businessUnitFileTranscription)))
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    public deleteFile(filePath): Promise<any> {
        return new Promise((resolve, reject) => {
            this.genHttp.icDelete('baseURL/services/' + this.auth.apiVersion + '/files?fileName=' + filePath)
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    aacDeleteFileInfo(fileId: string): Promise<any> {
        const buId = this.auth.businessUnit.businessUnitId;
        return new Promise((resolve, reject) => {
            const URL = 'licenseURL/registeredBusinessUnit/' + buId + '/file/' + fileId;
            this.genHttp.icDelete(URL)
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    public deleteFolder(folderName: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const deleteFolder = { folderName:  folderName };
            this.genHttp.icDelete('baseURL/services/' + this.auth.apiVersion + '/folders', deleteFolder)
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    aacDeleteFolderInfo(folderName?: string): Promise<AACFileInfo[]> {
        const buId = this.auth.businessUnit.businessUnitId;
        return new Promise((resolve, reject) => {
            const searchPayload = {filePath: folderName};
            const URL = 'licenseURL/registeredBusinessUnit/' + buId + '/files/path';
            this.genHttp.icDelete(URL, JSON.stringify(searchPayload))
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    aacGetFileList(path?: string): Promise<NICEFile[]> {
        if (!path) {
            path = '/';
        }
        return new Promise<NICEFile[]>((resolve, reject ) => {
            forkJoin([
                this.getFileList(path),
                this.aacFileListData(path)]
            ).subscribe(
                (res) => {
                    for (const d of res[0]) {
                        for (const c of res[1]) {
                            if (d.fileNameWithPath === c.filePath + '/' + c.fileName || (c.filePath === '/' && d.fileNameWithPath === c.filePath + c.fileName)) {
                                d.aacFileInfo = c;
                                break;
                            }
                        }
                    }
                    resolve(res[0].map(data => new NICEFile(data)));
                });
        });
    }

    getFileList(path?: string): Promise<NICEFile[]> {
        if (!path) {
            path = '/';
        }
        let listURL = 'baseURL/services/v20.0/scripts/files/search?rootFolder=' + encodeURIComponent(path);
        if (path.indexOf('/CallLog') === 0) {
            listURL = 'baseURL/services/v26.0/folders?folderName=' + encodeURIComponent(path);
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet(listURL)
                .pipe(map((response: any) => response.files.map(data => new NICEFile(data))))
                .subscribe((response: NICEFile[]) => {
                    resolve(response);
                }, reject);
        });
    }

    public getFile(filePath: string): Observable<any> {
        return this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/files?fileName=' + filePath);
        //return this.genHttp.icPost('http://localhost:5000/api/transcription/submit','{"filePath": "/TEST/7042426032.wav","businessUnitId": 176,"languageCode": "en-US"}');
    }

    public uploadFile(body): Promise<any> {
        return new Promise((resolve, reject) => {
            this.genHttp.icPost('baseURL/services/v20.0/files', body)
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    public submitFileForTranscription(filePath: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const body = {
                "filePath": filePath,
                "businessUnitId": this.currentUser.aacBusinessUnitId.toString(),
                "languageCode": "en-US"};
            this.genHttp.icPost('awsURL/submitTransciptionFile', JSON.stringify(body))
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        })
    }



    public getDispositions(skip?: number):Observable<NICEDisposition[]> {
        if(skip == null) {skip = 0;}
        let URL = 'baseURL/services/v30.0/dispositions?top=10000&skip=' + skip;
        if (this.dispositionsLastUpdatedDate)
        {
            URL= URL + '&updatedSince=' + - encodeURIComponent(this.dispositionsLastUpdatedDate.minus({ seconds: 60 }).toISO());
        }
        return this.genHttp.icGet(URL).pipe(
            switchMap((response: any) => {
                let deltaDisps: NICEDisposition[] = [];

                if (response?.dispositions && response.dispositions.length > 1) {
                    deltaDisps = response.dispositions.map((data: any) => new NICEDisposition(data));
                }

                deltaDisps.forEach(item => {
                    this.dispositionsMap.set(item.dispositionId, item);
                });

                if(response?._links?.next) {
                    // If there is a next url, make an another HTTP request
                    return concat(of(Array.from(this.dispositionsMap.values())), this.getDispositions(skip + 10000));
                }
                else if (response) {
                    //this.dispositionsLastUpdatedDate = DateTime.fromISO(response.lastPollTime, {zone: 'utc'});
                }
                return of(Array.from(this.dispositionsMap.values())as NICEDisposition[]);
            })
        );
    }


    public getDisposition(dispositionId: string): Promise<NICEDisposition> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/dispositions/' + dispositionId)
                .pipe(map((response: any) => new NICEDisposition(response)))
                .subscribe((response: NICEDisposition) => {
                    resolve(response);
                }, reject);
        });
    }

    public getDispositionSkills(dispositionId: string): Promise<NICESkill[]> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + 'v31.0' + '/dispositions/' + dispositionId + '/skills')
                .pipe(map((response: any) => response.skills.map(item => new NICESkill(item))))
                .subscribe((response: NICESkill[]) => {
                    resolve(response);
                }, reject);
        });
    }

    public getDispositionClassifications(): Promise<NICEDispClassification[]> {
        return new Promise((resolve, reject) => {
            const URL = 'baseURL/services/' + this.auth.apiVersion + '/dispositions/classifications';
            this.genHttp.icGet(URL)
                .pipe(map((response: any) => response.classificationResults.map(data => new NICEDispClassification(data))))
                .subscribe((response: NICEDispClassification[]) => {
                    //this._classifications.next(response);
                    resolve(response);
                }, reject);
        });
    }


    public getPointsOfContact(parameters?: string): Promise<NICEPOC[]> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            const URL = 'baseURL/services/' + this.auth.apiVersion + '/points-of-contact' + parameters;
            this.genHttp.icGet(URL)
                .pipe(
                    map((response: any) => {
                    const pocList = response.pointsOfContact.map(data => new NICEPOC(data));
                    //this._pocs.next(pocList);
                    return pocList;
                }),
                catchError((err,body) => {return of([])}),
                )
                .subscribe((response: NICEPOC[]) => {
                    resolve(response);
                }, reject);
        });
    }

    public getSkill(skillId: string): Promise<NICESkill> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/v32.0/skills/' + skillId)
                .pipe(map((response: any) => new NICESkill(response)))
                .subscribe((response: NICESkill) => {
                    resolve(response);
                }, reject);
        });
    }
    public getProfiles(){
        return this.genHttp.icGet('baseURL/services/v31.0/security-profiles')
            .pipe(
                map((response: any) => response.securityProfiles.filter(item => !item.isInternal  && item.SecurityProfileStatusName === 'Active'))
            );
    }

    public stopSkill(skillId: string, force?: boolean){
        return this.genHttp.icPost('baseURL/services/v31.0/skills/' + skillId + '/stop?force='+(force? 'true' : 'false'));
    }

    public startSkill(skillId: string){
        return this.genHttp.icPost('baseURL/services/v31.0/skills/' + skillId + '/start');
    }


    public getSkillAgents(skillId: string): Observable<NICESkillAgent[]> {
        return this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/agents')
            .pipe(map((response: any) => {
                if (response === null) {
                    return [];
                }
                return response.agentSkillAssignments.map(data => new NICESkillAgent(data));
            }));
    }


    public getSkills(skip?: number):Observable<NICESkill[]> {
        if(skip == null) {skip = 0;}
        const maxSkills = 10000;
        let URL = 'baseURL/services/v31.0/skills?top='+maxSkills+'&skip=' + skip;
        if (this.skillsLastUpdatedDate)
        {
            URL= URL + '&updatedSince=' + encodeURIComponent(this.skillsLastUpdatedDate.minus({ seconds: 60 }).toISO());
        }
        return this.genHttp.icGet(URL).pipe(
            switchMap((response: any) => {
                let deltaSkills: NICESkill[] = [];

                if (response?.skills && response.skills.length > 1) {
                    deltaSkills = response.skills.map((data: any) => new NICESkill(data));
                }

                deltaSkills.forEach(item => {

                    this.skillsMap.set(item.skillId, item);
                });

                if(response?._links?.next && deltaSkills.length == maxSkills) {
                    // If there is a next url, make an another HTTP request
                    return concat(of(Array.from(this.skillsMap.values())), this.getSkills(skip + maxSkills));
                }
                else if (response) {
                    this.skillsLastUpdatedDate = DateTime.fromISO(response.lastPollTime, {zone: 'utc'});
                }
                return of(Array.from(this.skillsMap.values())as NICESkill[]);
            })
        );
    }

    public updateFilePath(originalFile: NICEFile, newPath: string, overwrite: boolean): Observable<any> {
        const putFilePayload = {
            oldPath:  originalFile.fileNameWithPath,
            newPath:  newPath,
            overwrite:  overwrite
        };
        return this.genHttp.icPut('baseURL/services/' + this.auth.apiVersion + '/files', JSON.stringify(putFilePayload));
    }

    public getDialerGeneralSettings(skillId: string): Promise<NICEDialerGeneralSettings> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/general-settings')
                .pipe(map((response: any) => new NICEDialerGeneralSettings(response)))
                .subscribe((response: NICEDialerGeneralSettings) => {
                    resolve(response);
                }, reject);
        });
    }

    public updateDialerGeneralSettings(skillId: number, updateData: NICEDialerGeneralSettings): Observable<any> {
        const updatePayload = {
            generalSettings: updateData
        }
        return this.genHttp.icPut('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/general-settings',
            JSON.stringify(updatePayload));
    }

    public getDialerCPASettings(skillId: string): Promise<NICEDialerCPASettings> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/cpa-management')
                .pipe(map((response: any) => new NICEDialerCPASettings(response)))
                .subscribe((response: NICEDialerCPASettings) => {
                    resolve(response);
                }, reject);
        });
    }

    public getDialerParameters(skillId: string): Promise<NICEDialerParameters> {
        const $dialerData = forkJoin({
            // uncomment below when GET becomes available
            dialerCustomFields: this.getCustomDialerFields(),
            dialerParameters: this.genHttp.icGet('baseURL/services/v31.0/skills/' + skillId + '/parameters')
                .pipe(catchError((err,body) => {return of(null)}  )),
        });


        return new Promise((resolve, reject) => {
            $dialerData
                .pipe(map((response: any) => {
                    if (response.dialerParameters === null) {
                        return {listManagementSettings:null,cadenceSettings:null,filterSettings:null};
                    }
                   return new NICEDialerParameters(response.dialerParameters, response.dialerCustomFields)
                }))
                .subscribe((response: NICEDialerParameters) => {
                    resolve(response);
                }, reject);
        });
    }

    getCustomDialerFields() {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/v32.0/custom-data-definition')
                .pipe(map((response: any) => response.results))
                .subscribe((response: NICEDialerParameters) => {
                    resolve(response);
                }, reject);
        });
    }


    public updateDialerCPASettings(skillId: number, updateData: NICEDialerCPASettings): Observable<any> {
        const updatePayload = {cpaSettings:updateData}
        return this.genHttp.icPut('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/cpa-management',
            JSON.stringify(updatePayload));
    }

    public getDialerXSSettings(skillId: string): Promise<NICEDialerXSSettings> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/xs-settings')
                .pipe(map((response: any) => new NICEDialerXSSettings(response)))
                .subscribe((response: NICEDialerXSSettings) => {
                    resolve(response);
                }, reject);
        });
    }

    public updateDialerXSSettings(skillId: number, updateData: NICEDialerXSSettings): Observable<any> {
        const updatePayload = {xsSettings:updateData}
        return this.genHttp.icPut('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/xs-settings',
            JSON.stringify(updatePayload));
    }

    public getDialerDeliverySettings(skillId: string): Promise<NICEDialerDeliverySettings> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/delivery-preferences')
                .pipe(map((response: any) => new NICEDialerDeliverySettings(response)))
                .subscribe((response: NICEDialerDeliverySettings) => {
                    resolve(response);
                }, reject);
        });
    }

    public updateDialerDeliverySettings(skillId: number, updateData: NICEDialerDeliverySettings): Observable<any> {
        const updatePayload = {deliveryPreferences:updateData}
        return this.genHttp.icPut('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/delivery-preferences',
            JSON.stringify(updatePayload));
    }

    public getDialerRetrySettings(skillId: string): Promise<NICEDialerRetrySettings> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/retry-settings')
                .pipe(map((response: any) => new NICEDialerRetrySettings(response)))
                .subscribe((response: NICEDialerRetrySettings) => {
                    resolve(response);
                }, reject);
        });
    }

    public updateDialerRetrySettings(skillId: number, updateData: NICEDialerRetrySettings): Observable<any> {
        const updatePayload = {retrySettings:updateData}
        return this.genHttp.icPut('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/retry-settings',
            JSON.stringify(updatePayload));
    }


    public getDialerScheduleSettings(skillId: string): Promise<NICEDialerScheduleSettings> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/schedule-settings')
                .pipe(map((response: any) => new NICEDialerScheduleSettings(response)))
                .subscribe((response: NICEDialerScheduleSettings) => {
                    resolve(response);
                }, reject);
        });
    }

    public updateDialerScheduleSettings(skillId: number, updateData: NICEDialerScheduleSettings): Observable<any> {
        const updatePayload = {scheduleSettings:updateData}
        return this.genHttp.icPut('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/schedule-settings',
            JSON.stringify(updatePayload));
    }


    public getDialerTimezoneSettings(skillId: string): Promise<NICEDialerTimezoneSettings> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/time-zones')
                .pipe(map((response: any) => new NICEDialerTimezoneSettings(response)))
                .subscribe((response: NICEDialerTimezoneSettings) => {
                    resolve(response);
                }, reject);
        });
    }

    public updateDialerTimezoneSettings(skillId: number, updateData: NICEDialerTimezoneSettings): Observable<any> {
        return this.genHttp.icPut('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/time-zones', JSON.stringify(updateData));
    }

    public updateDialerListMgmtSettings(skillId: number, updateData: NICEDialerListMgmtSettings): Observable<any> {
        return this.genHttp.icPut('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/list-management', JSON.stringify(updateData));
    }
    // public updateDialerFilterSettings(skillId: number, updateData: NICEDialerListMgmtSettings): Observable<any> {
    //     return this.genHttp.icPut('baseURL/services/' + this.auth.apiVersion + '/skills/' + skillId + '/parameters/filter', JSON.stringify(updateData));
    // }
    public updateDialerCadenceSettings(skillId: number, updateData: NICEDialerListMgmtSettings): Observable<any> {
        return this.genHttp.icPut('baseURL/services/v32.0/skills/' + skillId + '/parameters/cadence-settings', JSON.stringify(updateData));
    }

    public createSkill(skill: any): Observable<any> {
        return this.genHttp.icPost('baseURL/services/' + this.auth.apiVersion + '/skills/', JSON.stringify(skill));
    }

    public updateSkill(skill: any): Observable<any> {
        const copySkill = Object.assign({}, skill);
        const skill2update = {skill: copySkill};
        const updateSkillId = copySkill.skillId;
        delete copySkill['skillId'];
        delete copySkill['mediaTypeName'];
        delete copySkill['campaignName'];
        delete copySkill['stateNameACW'];
        delete copySkill['acwPostTimeoutStateName'];
        if (copySkill.callSuppressionScriptId < 1)
        {
            copySkill.callSuppressionScriptId = null
        }
        if (copySkill.dispositions)
        {
            let dispPriority = 1;
            for (const disposition of copySkill.dispositions) {
                disposition.priority = dispPriority;
                dispPriority++;
            }
        }
        return this.genHttp.icPut('baseURL/services/v31.0/skills/' + updateSkillId, JSON.stringify(skill2update)).pipe(
            catchError((err) => {
                // Get the `icstatusdescription` from headers (if applicable)
                const icStatusDescription = err.headers?.get('icstatusdescription') || '';

                // Modify the error description based on the error's status code
                let errorDescription = '';
                switch (err.status) {
                    case 400:
                        errorDescription = "Bad request. Invalid or unauthorized parameter.";
                        break;
                    case 401:
                        errorDescription = "Invalid or expired token.";
                        break;
                    case 403:
                        errorDescription = "Given skill is restricted. Check security profile permissions.";
                        break;
                    case 404:
                        errorDescription = "Not Found.";
                        break;
                    case 409:
                        errorDescription = "Cannot update a running skill.";
                        break;
                    case 500:
                        errorDescription = "Skill setup failed. Skill cannot be marked active.";
                        break;
                    default:
                        errorDescription = "An unknown error occurred. Please try again later.";
                        break;
                }
                // Use the `icStatusDescription` if it exists
                if (icStatusDescription) {
                    errorDescription += ` Additional info: ${icStatusDescription}`;
                }
                // Modify the error object and re-throw it
                const modifiedError = {
                    ...err,
                    message: errorDescription, // Update the description in the error object
                };

                return throwError(() => modifiedError);
            }));

    }

    public getCampaigns(parameters?: string): Promise<NICECampaign[]> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/campaigns' + parameters)
                .pipe(map((response: any) => {
                    let campaignList = null;
                    if (response.resultSet.totalRecords > 0) {
                        campaignList = response.resultSet.campaigns.map(data => new NICECampaign(data));
                    }
                    //this._campaigns.next(campaignList);
                    return campaignList;
                }))
                .subscribe((response: NICECampaign[]) => {
                    resolve(response);
                }, reject);
        });
    }

    public getCampaign(campaignId: string): Promise<NICECampaign> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/campaigns/' + campaignId)
                .pipe(map((response: any) => new NICECampaign(response.campaign)))
                .subscribe((response: NICECampaign) => {
                    resolve(response);
                }, reject);
        });
    }

    public getUnavailableCodes(parameters?: string): Promise<NICEUnavailableCode[]> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/unavailable-codes' + parameters)
                .pipe(map((response: any) => {
                    const unavailableCodeList = response.unavailableCodes.map(data => new NICEUnavailableCode(data));
                    //this._unavailableCodes.next(unavailableCodeList);
                    return unavailableCodeList;
                }))
                .subscribe((response: NICEUnavailableCode[]) => {
                    resolve(response);
                }, reject);
        });
    }


    public runDataDownload(reportId: string, startDate: Date, endDate: Date): Promise<NICEDataDownload> {
        return new Promise((resolve, reject) => {
            this.genHttp.icPost('baseURL/services/v17.0/report-jobs/datadownload/' + reportId
                + '?fileName=file.xml'
                + '?fileType=xml'
                + '&startDate=' + startDate.toISOString()
                + '&endDate=' + endDate.toISOString()
                + '&saveAsFile=false'
                 + '&includeHeaders=true'
                , ''
            )
                .pipe(map((response: any) => new NICEDataDownload(response)))
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }

    public spawnScript(scriptName: string, parameters: any): Promise<string> {
        return new Promise((resolve, reject) => {
            this.genHttp.icPost('baseURL/services/v19.0/scripts/scriptId/start' , JSON.stringify(parameters))
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }

    public getHoursOfOperation(parameters?: string): Promise<NICEHoursOfOperation[]> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/hours-of-operation' + parameters)
                .pipe(map((response: any) => {
                    if (response !== null) {
                        return response.resultSet.hoursOfOperationProfiles.map(data => new NICEHoursOfOperation(data));
                    }else{
                        return null;
                    }
                }))
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }

    public getMediaTypes():Observable<NICEMediaTypes[]> {
        if (this.mediaTypes.length > 0 ) {
            return of (this.mediaTypes);
        }
        const URL = 'baseURL/services/' + this.auth.apiVersion + '/media-types';
        return this.genHttp.icGet(URL).pipe(
            map((response: any) => response.mediaTypes.map(data => new NICEMediaTypes(data))),
            tap((mtypes)=>{
                this.mediaTypes = mtypes;
            }
            )
        );
    }

    public getTeams(parameters?: string): Promise<NICETeam[]> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/teams' + parameters)
                .pipe(map((response: any) => {
                    const teamList = response.teams.map(data => new NICETeam(data));
                    //this._teams.next(teamList);
                    return teamList;
                }))
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public getAddressBooks(): Promise<NICEAddressBook[]> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/address-books')
                .pipe(map((response: any) => response.addressBooks.map(data => new NICEAddressBook(data))))
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public postDNCGroup(parameters?: string): Promise<NICEDNCGroup[]> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icPost('baseURL/services/' + this.auth.apiVersion + '/dnc-groups', parameters)
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public getScrubbedDNCSkills(parameters?: string): Promise<NICEDNCScrubbedSkills[]> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + parameters + '/scrubbed-skills')
                .pipe(map((response: any) => {
                    if (response === null)
                    {
                        return [];
                    }
                    return response.scrubbedSkills.map(data => new NICEDNCScrubbedSkills(data));
                }))
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public postScrubbedDNCSkills(parameters?: any, skillId?: any): Promise<string> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icPost('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + parameters + '/scrubbed-skills/' + skillId)
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public deleteScrubbedDNCSkills(parameters: any, skillId: any): Promise<string> {
        return new Promise((resolve, reject) => {
            this.genHttp.icDelete('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + parameters + '/scrubbed-skills/' + skillId)
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public getContributingDNCSkills(parameters?: string): Promise<NICEDNCContributingSkills[]> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + parameters + '/contributing-skills')
                .pipe(map((response: any) => {
                    if (response === null)
                    {
                        return [];
                    }
                    return response.contributingSkills.map(data => new NICEDNCContributingSkills(data));
                }))
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public postContributingDNCSkills(parameters?: any, skillId?: any): Promise<string> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icPost('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + parameters + '/contributing-skills/' + skillId)
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public deleteContributingDNCSkills(parameters: any, skillId: any): Promise<string> {
        return new Promise((resolve, reject) => {
            this.genHttp.icDelete('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + parameters + '/contributing-skills/' + skillId)
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public getDNCRecords(parameters: string, paramFields?: string): Promise<NICEDNCRecord[]> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + parameters + '/records' + paramFields)
                .pipe(map((response: any) => response.dncList.dncRecords.map(data => new NICEDNCRecord(data))))
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }

    public getAllDNCRecords(groupId: string): Observable<NICEDNCRecord[]> {
        let skip = 0;
        const top = 10000;
        return this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + groupId + '/records?skip='+ skip.toString() + '&top=' + top.toString())
            .pipe(
                // we recursively call the GET requests until there is no 'next' url
                expand((apiResponse) => {
                        skip = top + skip;
                        if (apiResponse.dncList && apiResponse.dncList.dncRecords.length === top)
                        {
                            return this.genHttp.icGet('baseURL/services/' +
                                this.auth.apiVersion +
                                '/dnc-groups/' +
                                groupId +
                                '/records?skip='+
                                skip.toString()+
                                '&top='+
                                top.toString());
                        }
                        else
                        {
                            return EMPTY;
                        }
                    }
                ),
                // we map the api response to the data we actually want to return
                map(apiResponse => apiResponse.dncList.dncRecords),
                // we reduce the data of all GET requests to a single array
                reduce((accData, data) => accData.concat(data), [])
            );
}



    public postDNCRecords(parameters: any, body: string): Promise<NICEDNCGroup[]> {
        return new Promise((resolve, reject) => {
            this.genHttp.icPost('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + parameters + '/records', body)
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public deleteDNCRecords(parameters: any, body: string): Promise<string> {
        return new Promise((resolve, reject) => {
            this.genHttp.icDelete('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + parameters + '/records', body)
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }
    public getDNCGroups(parameters?: string): Promise<NICEDNCGroup[]> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/dnc-groups' + parameters)
                .pipe(map((response: any) => response.resultSet.dncGroups.map(data => new NICEDNCGroup(data))))
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }

    public getDNCGroup(groupId: string): Promise<NICEDNCGroup> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/dnc-groups/' + groupId)
                .pipe(map((response: any) => new NICEDNCGroup(response.resultSet.dncGroups[0])))
                .subscribe({
                    next: (response) => {
                        resolve(response);
                    },
                    error: (err) => {
                        console.error(err);
                        reject(err);
                    }
                });
        });
    }

    public getAgentList(skip?: number):Observable<NICEAgent[]> {
            if(skip == null) {skip = 0;}
        let URL = 'baseURL/services/v30.0/agents?top=10000&skip=' + skip;
        if (this.agentsLastUpdatedDate)
        {
            URL= URL + '&updatedSince=' + encodeURIComponent(this.agentsLastUpdatedDate.minus({ seconds: 60 }).toISO());
        }
        return this.genHttp.icGet(URL).pipe(
            switchMap((response: any) => {
                let deltaAgents: NICEAgent[] = [];

                if (response?.agents && response.agents.length > 1) {
                    deltaAgents = response.agents.map((data: any) => new NICEAgent(data));
                }

                deltaAgents.forEach(item => {
                        this.agentsMap.set(item.agentId, item);
                });

                if(response?._links?.next) {
                    // If there is a next url, make an another HTTP request
                    return concat(of(Array.from(this.agentsMap.values())), this.getAgentList(skip + 10000));
                }
                else if (response) {
                    this.agentsLastUpdatedDate = DateTime.fromISO(response.lastPollTime, {zone: 'utc'});
                }
                return of(Array.from(this.agentsMap.values())as NICEAgent[]);
            })
        );
    }


    public getTimeZones(): Promise<NICETimeZone[]> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/timezones')
                .pipe(map((response: any) => {
                    const tzList = response.timeZones.map(data => new NICETimeZone(data));
//                    this._teams.next(tzList);
                    return tzList;
                }))
                .subscribe((response: NICETimeZone[]) => {
                    resolve(response);
                }, reject);
        });
    }

    public getCountries(): Promise<NICECountry[]> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/countries')
                .pipe(map((response: any) => {
                    const countryList = response.countries.map(data => new NICECountry(data));
                    return countryList;
                }))
                .subscribe((response: NICECountry[]) => {
                    resolve(response);
                }, reject);
        });
    }

    public getCountryStates(countryId): Promise<NICEState[]> {
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/countries/' + countryId + '/states')
                .pipe(map((response: any) => {
                    let stateList: NICEState[] = [];
                    if (response && response.resultSet){
                        stateList = response.resultSet.states.map(data => new NICEState(data));
                    }
                    return stateList;
                }))
                .subscribe((response: NICEState[]) => {
                    resolve(response);
                }, reject);
        });
    }

    public getSkillSummaryReport(parameters?: string): Promise<any> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/skills/summary' + parameters)
                .pipe(map((response: any) => response))
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    public getTeamsPerformanceTotalReport(parameters?: string): Promise<any> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/teams/performance-total' + parameters)
                .pipe(map((response: any) => response))
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    public getAgentsPerformanceTotalReport(parameters?: string): Promise<any> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/agents/performance' + parameters)
                .pipe(map((response: any) => response))
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    public getContactsCompletedReport(parameters?: string): Promise<any> {
        if (!parameters) {
            parameters = '';
        }
        return new Promise((resolve, reject) => {
            this.genHttp.icGet('baseURL/services/' + this.auth.apiVersion + '/contacts/completed' + parameters)
                .pipe(map((response: any) => response))
                .subscribe((response: any) => {
                    resolve(response);
                }, reject);
        });
    }

    public getDigitalTags(): Observable<NICEDigitalTag[]> {
            return this.genHttp.icGet('apiBaseURL/dfo/3.0/tags')
            .pipe(map((response: any) => {
                let tagList: NICEDigitalTag[] = [];
                if (response && response.data){
                    tagList = response.data.map(data => new NICEDigitalTag(data));
                }
                return tagList;
            }));
    }

    public updateDigitalTag(tagId: number, title: string,color:string): Observable<any> {
        const updatePayload = {title,color};
        return this.genHttp.icPut('apiBaseURL/dfo/3.0/tags/'+tagId,
            JSON.stringify(updatePayload));
    }

    public createDigitalTag(title: string,color:string): Observable<any> {
        const updatePayload = {title,color};
        return this.genHttp.icPut('apiBaseURL/dfo/3.0/tags',
            JSON.stringify(updatePayload));
    }


    public getUserFilter(): string {
        return this.userFilterValue;
    }

    public putUserFiler(filter: string): void {
        this.userFilterValue = filter;
    }

    public getDigitalTagFilter(): string {
        return this.digitalTagFilterValue;
    }
    public putDigitalTagFilter(filter: string): void {
        this.digitalTagFilterValue = filter;
    }

    public getSkillFiler(): string {
        return this.skillFilterValue;
    }

    public putSkillFilter(filter: string): void {
        this.skillFilterValue = filter;
    }
    public getCampaignFilter(): string {
        return this.campaignFilterValue;
    }

    public putCampaignFilter(filter: string): void {
        this.campaignFilterValue = filter;
    }
    public getScriptFilter(): string {
        return this.scriptFilterValue;
    }

    public putScriptFilter(filter: string): void {
        this.scriptFilterValue = filter;
    }

    public getDNCnumberFilter(): string {
        return this.dNCnumberValue;
    }

    public putDNCnumberFilter(filter: string): void {
        this.dNCnumberValue = filter;
    }

    public getDNCGroupsFilter(): string {
        return this.dNCgroupValue;
    }
    public putDNCGroupsFilter(filter: string): void {
        this.dNCgroupValue = filter;
    }


}
